import typia from 'typia';
import { isEqual } from 'lodash';
import { getContextComponentIds, hasStartQuery, isTraversalLoadedState, type APIComponentAttributes, type ArdoqId, type LoadedState, type TraversalLoadedState, } from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { logWarn } from '@ardoq/logging';
import { anyType } from '@ardoq/global-consts';
import { setStartQueryForLoadedState, setStartSetForLoadedState, } from 'loadedState/setStartContextForLoadedState';
import { getTypeQuery } from 'canvas/utils/getTypeQuery';
import { pathOperations } from 'traversals/pathOperations';
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import type { AiControlledCommand, CommandRejection, } from 'aiControlledCommands/types';
import { defineAiControlledCommand, type CommandValidationResult, } from 'aiControlledCommands/defineAiControlledCommand';
import { getChangeableTraversalDatasets, getViewpointName, isChangeableTraversalDataset, type ViewpointsById, } from 'aiControlledCommands/viewpoint/traversalDatasets';
import { VIEWPOINT_DOMAIN } from 'aiControlledCommands/viewpoint/domain';
import { TRAVERSAL_CONTEXT_DESCRIPTION, WHEN_TO_USE } from './descriptions';
// typia compiles the JSDoc here into the command's JSON schema, so these
// comments are LLM-facing — keep them accurate.
type StartFromComponents = {
    /**
     * Start the dataset from the components in 'componentIds' — the picker
     * the user has in the context dropdown.
     */
    mode: 'components';
    /** The 'datasetId' of the dataset to move, copied from the context. */
    datasetId: string;
    /**
     * Ids of the components the dataset should start from, all of its
     * 'startComponentType'. This replaces the whole context, so for "add X"
     * or "remove Y" work out the current one first and send it complete —
     * which needs 'contextComponents' to list the whole context, so check
     * it against 'contextComponentCount'.
     */
    componentIds: string[];
};
type StartFromAllOfType = {
    /**
     * Start the dataset from every component of its 'startComponentType' —
     * the "select all" in the context dropdown. Use it for "all"/"every"
     * requests instead of listing ids.
     */
    mode: 'allOfType';
    /** The 'datasetId' of the dataset to move, copied from the context. */
    datasetId: string;
};
export type ChangeTraversalContextPayload = StartFromComponents | StartFromAllOfType;
type ContextComponent = {
    id: ArdoqId;
    name: string | null;
};
type DatasetDescriptor = {
    datasetId: string;
    viewpointName: string | null;
    startComponentType: string | null;
    contextComponentCount: number;
    contextComponents: ContextComponent[];
    contextMode: 'components' | 'allOfType' | 'none';
};
export type OpenDatasetsContext = {
    datasets: DatasetDescriptor[];
};
const isTraversalDataset = (loadedState: LoadedState): loadedState is TraversalLoadedState => isTraversalLoadedState(loadedState);
type ComponentsById = Map<ArdoqId, APIComponentAttributes>;
const toComponentsById = (loadedStates: LoadedState[]): ComponentsById => new Map(loadedStates.flatMap(({ scopeData }) => scopeData.components.map(component => [component._id, component] as const)));
const isOfOtherType = (componentsById: ComponentsById, startComponentType: string) => (id: ArdoqId): boolean => {
    const typeName = componentsById.get(id)?.type;
    return Boolean(typeName) && typeName !== startComponentType;
};
const getSingleStartSetType = (componentsById: ComponentsById, startSetResult: ArdoqId[]): string | undefined => {
    const startSetType = startSetResult
        .map(id => componentsById.get(id)?.type)
        .find(isTruthy);
    if (!startSetType) {
        return undefined;
    }
    return startSetResult.some(isOfOtherType(componentsById, startSetType))
        ? undefined
        : startSetType;
};
const getStartComponentType = (componentsById: ComponentsById, loadedState: TraversalLoadedState): string | undefined => {
    const { paths, startSetExpansion, startSetExpansionFromType } = loadedState.data;
    if (startSetExpansion &&
        startSetExpansionFromType &&
        startSetExpansionFromType !== anyType) {
        return startSetExpansionFromType;
    }
    const pathsStartType = pathOperations.getStartTypeFromPaths(paths);
    if (pathsStartType && pathsStartType !== anyType) {
        return pathsStartType;
    }
    return getSingleStartSetType(componentsById, loadedState.componentIdsAndReferences?.startSetResult ?? []);
};
const getMismatchedComponentIds = (componentsById: ComponentsById, componentIds: ArdoqId[], startComponentType: string | undefined): ArdoqId[] => startComponentType
    ? componentIds.filter(isOfOtherType(componentsById, startComponentType))
    : [];
const toContextComponent = (componentsById: ComponentsById) => (id: ArdoqId): ContextComponent => ({
    id,
    name: componentsById.get(id)?.name ?? null,
});
const MAX_LISTED_CONTEXT_COMPONENTS = 50;
const getContextComponents = (componentsById: ComponentsById, loadedState: TraversalLoadedState): Pick<DatasetDescriptor, 'contextComponentCount' | 'contextComponents' | 'contextMode'> => {
    const contextComponentIds = getContextComponentIds(loadedState.data.componentSelection, loadedState.componentIdsAndReferences?.startSetResult ?? []);
    return {
        contextComponentCount: contextComponentIds.length,
        contextComponents: contextComponentIds
            .slice(0, MAX_LISTED_CONTEXT_COMPONENTS)
            .map(toContextComponent(componentsById)),
        contextMode: loadedState.data.componentSelection.startSet.length
            ? 'components'
            : hasStartQuery(loadedState.data.componentSelection)
                ? 'allOfType'
                : 'none',
    };
};
const toDatasetDescriptor = (viewpointsById: ViewpointsById, componentsById: ComponentsById) => (loadedState: TraversalLoadedState): DatasetDescriptor => ({
    datasetId: loadedState.uuid,
    viewpointName: getViewpointName(viewpointsById, loadedState),
    startComponentType: getStartComponentType(componentsById, loadedState) ?? null,
    ...getContextComponents(componentsById, loadedState),
});
const getSingleTraversalDatasetOfViewpoint = (loadedStates: LoadedState[], viewpointId: string): TraversalLoadedState | undefined => {
    const datasetsOfViewpoint = getChangeableTraversalDatasets(loadedStates).filter(({ traversalId }) => traversalId === viewpointId);
    return datasetsOfViewpoint.length === 1 ? datasetsOfViewpoint[0] : undefined;
};
const getTraversalDataset = (loadedStates: LoadedState[], datasetId: string): TraversalLoadedState | undefined => {
    const loadedState = loadedStates.find(state => state.uuid === datasetId);
    if (!loadedState) {
        return getSingleTraversalDatasetOfViewpoint(loadedStates, datasetId);
    }
    return isTraversalDataset(loadedState) ? loadedState : undefined;
};
export const hasTraversalDataset = (loadedStates: LoadedState[]): boolean => loadedStates.some(isChangeableTraversalDataset);
export const buildTraversalContextCommand = (loadedStates: LoadedState[], viewpointsById: ViewpointsById): AiControlledCommand<typeof traversalContextCommandDefinition> => ({
    ...traversalContextCommandDefinition,
    context: {
        datasets: getChangeableTraversalDatasets(loadedStates).map(toDatasetDescriptor(viewpointsById, toComponentsById(loadedStates))),
    },
});
type ResolvedTraversalContextChange = (StartFromComponents & {
    loadedState: TraversalLoadedState ;
}) | (StartFromAllOfType & {
    loadedState: TraversalLoadedState;
    startComponentType: string;
});
const rejectTraversalContextChange = (reason: string): CommandRejection => ({
    status: 'rejected',
    reason,
});
const reportContextNotChanged = (code: string, context: Record<string, unknown>): void => {
    logWarn(new Error('AI could not change the traversal context'), null, {
        tags: { team: 'insight' },
        code,
        ...context,
    });
};
const resolveTraversalContextChange = (payload: ChangeTraversalContextPayload, loadedStates: LoadedState[]): CommandValidationResult<ResolvedTraversalContextChange> => {
    const loadedState = getTraversalDataset(loadedStates, payload.datasetId);
    if (!loadedState) {
        reportContextNotChanged('ai_traversal_context_dataset_gone', {
            openDatasetCount: getChangeableTraversalDatasets(loadedStates).length,
        });
        return rejectTraversalContextChange("No open dataset matches this datasetId. Use a 'datasetId' from the context.");
    }
    if (loadedState.isHidden) {
        reportContextNotChanged('ai_traversal_context_dataset_hidden', {});
        return rejectTraversalContextChange('The dataset is hidden, so its context cannot be changed.');
    }
    const resolvedPayload = {
        ...payload,
        datasetId: loadedState.uuid,
        loadedState,
    };
    const componentsById = toComponentsById(loadedStates);
    const startComponentType = getStartComponentType(componentsById, loadedState);
    if (resolvedPayload.mode === 'components') {
        const mismatchedComponentIds = getMismatchedComponentIds(componentsById, resolvedPayload.componentIds, startComponentType);
        if (mismatchedComponentIds.length) {
            reportContextNotChanged('ai_traversal_context_type_mismatch', {
                startComponentType,
                requestedCount: resolvedPayload.componentIds.length,
                mismatchedCount: mismatchedComponentIds.length,
            });
            return rejectTraversalContextChange(`Some of the requested components are not of the dataset's start type '${startComponentType}'.`);
        }
        return { status: 'valid', payload: resolvedPayload };
    }
    if (!startComponentType) {
        reportContextNotChanged('ai_traversal_context_no_start_type', {
            hasStartSetExpansion: Boolean(loadedState.data.startSetExpansion),
            startSetExpansionFromType: loadedState.data.startSetExpansionFromType ?? null,
            pathCount: loadedState.data.paths.length,
        });
        return rejectTraversalContextChange('The dataset has no single start component type, so it cannot start from all components of one type.');
    }
    return {
        status: 'valid',
        payload: { ...resolvedPayload, startComponentType },
    };
};
const executeChangeTraversalContext = (change: ResolvedTraversalContextChange): void => {
    if (change.mode === 'components') {
        setStartSetForLoadedState(change.loadedState, change.componentIds);
        return;
    }
    setStartQueryForLoadedState(change.loadedState, getTypeQuery(change.startComponentType));
};
export const traversalContextCommandDefinition = defineAiControlledCommand<ResolvedTraversalContextChange, OpenDatasetsContext>({
    name: AiControlledCommandId.CHANGE_TRAVERSAL_CONTEXT,
    requiresAppShell: true,
    domain: VIEWPOINT_DOMAIN,
    description: WHEN_TO_USE,
    parameters: {
        version: "3.1",
        components: {
            schemas: {
                ChangeTraversalContextPayload: {
                    oneOf: [
                        {
                            $ref: "#/components/schemas/StartFromComponents"
                        },
                        {
                            $ref: "#/components/schemas/StartFromAllOfType"
                        }
                    ]
                },
                StartFromComponents: {
                    type: "object",
                    properties: {
                        mode: {
                            "const": "components",
                            description: "Start the dataset from the components in 'componentIds' \u2014 the picker\nthe user has in the context dropdown."
                        },
                        datasetId: {
                            type: "string",
                            description: "The 'datasetId' of the dataset to move, copied from the context."
                        },
                        componentIds: {
                            type: "array",
                            items: {
                                type: "string"
                            },
                            description: "Ids of the components the dataset should start from, all of its\n'startComponentType'. This replaces the whole context, so for \"add X\"\nor \"remove Y\" work out the current one first and send it complete \u2014\nwhich needs 'contextComponents' to list the whole context, so check\nit against 'contextComponentCount'."
                        }
                    },
                    required: [
                        "mode",
                        "datasetId",
                        "componentIds"
                    ]
                },
                StartFromAllOfType: {
                    type: "object",
                    properties: {
                        mode: {
                            "const": "allOfType",
                            description: "Start the dataset from every component of its 'startComponentType' \u2014\nthe \"select all\" in the context dropdown. Use it for \"all\"/\"every\"\nrequests instead of listing ids."
                        },
                        datasetId: {
                            type: "string",
                            description: "The 'datasetId' of the dataset to move, copied from the context."
                        }
                    },
                    required: [
                        "mode",
                        "datasetId"
                    ]
                }
            }
        },
        schema: {
            $ref: "#/components/schemas/ChangeTraversalContextPayload"
        }
    } as import("typia").IJsonSchemaUnit<"3.1">,
    contextDescription: TRAVERSAL_CONTEXT_DESCRIPTION,
    isContext: (context): context is OpenDatasetsContext => (() => { const _io0 = (input: any): boolean => Array.isArray(input.datasets) && input.datasets.every((elem: any) => "object" === typeof elem && null !== elem && _io1(elem)); const _io1 = (input: any): boolean => "string" === typeof input.datasetId && (null === input.viewpointName || "string" === typeof input.viewpointName) && (null === input.startComponentType || "string" === typeof input.startComponentType) && "number" === typeof input.contextComponentCount && (Array.isArray(input.contextComponents) && input.contextComponents.every((elem: any) => "object" === typeof elem && null !== elem && _io2(elem))) && ("components" === input.contextMode || "allOfType" === input.contextMode || "none" === input.contextMode); const _io2 = (input: any): boolean => "string" === typeof input.id && (null === input.name || "string" === typeof input.name); return (input: any): input is OpenDatasetsContext => "object" === typeof input && null !== input && _io0(input); })()(context),
    validate: (rawPayload, _context, { loadedStates }) => {
        if (!(() => { const _io0 = (input: any): boolean => "components" === input.mode && "string" === typeof input.datasetId && (Array.isArray(input.componentIds) && input.componentIds.every((elem: any) => "string" === typeof elem)); const _io1 = (input: any): boolean => "allOfType" === input.mode && "string" === typeof input.datasetId; const _iu0 = (input: any): any => (() => {
            if ("components" === input.mode)
                return _io0(input);
            else if ("allOfType" === input.mode)
                return _io1(input);
            else
                return false;
        })(); return (input: any): input is ChangeTraversalContextPayload => "object" === typeof input && null !== input && _iu0(input); })()(rawPayload)) {
            return rejectTraversalContextChange('The AI returned an invalid traversal context.');
        }
        if (rawPayload.mode === 'components' &&
            rawPayload.componentIds.length === 0) {
            return rejectTraversalContextChange("A dataset cannot start from no components. Ask the user which components to start from, or whether to start from every component of its start type ('allOfType').");
        }
        return resolveTraversalContextChange(rawPayload, loadedStates);
    },
    execute: executeChangeTraversalContext,
    isApplied: (payload, { datasets }) => {
        const dataset = datasets.find(({ datasetId }) => datasetId === payload.datasetId);
        if (!dataset || dataset.contextMode !== payload.mode)
            return false;
        if (payload.mode === 'allOfType')
            return true;
        return (dataset.contextComponentCount === payload.componentIds.length &&
            isEqual(dataset.contextComponents.map(({ id }) => id), payload.componentIds.slice(0, MAX_LISTED_CONTEXT_COMPONENTS)));
    },
});
